import { useEffect } from "react";
import PageHeader from "@/components/PageHeader";
import SectionHeading from "@/components/SectionHeading";
import Reveal from "@/components/Reveal";
import CTASection from "@/components/CTASection";
import { Compass, PenTool, Rocket, Eye, MessageSquare, RefreshCw, Target } from "lucide-react";
import { TEAM, teamSlug } from "@/data/team";

const APPROACH = [
  {
    icon: Compass,
    title: "Strategy",
    text: "Every project begins with understanding — the business, the audience, the goal. Direction before motion.",
  },
  {
    icon: PenTool,
    title: "Creativity",
    text: "Ideas and craft turn strategy into content, campaigns and experiences people actually notice.",
  },
  {
    icon: Rocket,
    title: "Execution",
    text: "Plans mean nothing without shipping. We launch, monitor and keep improving — relentlessly.",
  },
];

const BELIEFS = [
  { icon: Eye, title: "Understanding before acting", text: "We never prescribe before we diagnose. Discovery comes first, always." },
  { icon: Target, title: "Strategy before execution", text: "Tactics without direction waste budgets. Every action traces back to a plan." },
  { icon: MessageSquare, title: "Quality communication", text: "Clear, honest, regular communication is part of the service — not an extra." },
  { icon: RefreshCw, title: "Continuous improvement", text: "Digital never stands still. Neither do the strategies we build." },
];

const About = () => {
  useEffect(() => {
    document.title = "About — Click World Online";
  }, []);
  return (
    <>
      <PageHeader
        eyebrow="About Us"
        title={<>The people behind <span className="text-electric">the click.</span></>}
        text="Click World Online is a digital marketing agency based in Mansa, Punjab — built on strategy, creativity and honest work."
      />

      <section data-testid="our-story-section" className="bg-mist text-navy dot-grid-light">
        <div className="max-w-8xl mx-auto px-6 lg:px-12 py-24 lg:py-36">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <SectionHeading dark={false} eyebrow="Our Story" title={<>Local roots. <span className="text-brand">Digital reach.</span></>} />
            </div>
            <div className="lg:col-span-8 space-y-6 text-navy/75 leading-relaxed text-base md:text-lg max-w-3xl">
              <Reveal>
                <p>
                  Founded on 30 April 2017, Click World Online was built on a simple observation: businesses everywhere — from Mansa to metros —
                  deserve digital marketing that is thoughtful, professional and genuinely connected to their goals.
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <p>
                  From our base in Mansa, Punjab, we partner with businesses across industries — retail, education,
                  healthcare, hospitality, manufacturing, political parties and beyond — helping them show up online with the same quality
                  they deliver offline.
                </p>
              </Reveal>
              <Reveal delay={0.18}>
                <p>
                  Our work spans strategy, search, advertising, social media, content, websites and immersive 360°
                  experiences — always as one connected system, never as isolated tasks.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section data-testid="our-approach-section" className="bg-navy grain relative">
        <div className="max-w-8xl mx-auto px-6 lg:px-12 py-24 lg:py-36">
          <SectionHeading
            eyebrow="Our Approach"
            title={<>Three disciplines. <span className="text-electric">One system.</span></>}
            text="Strategy, creativity and execution aren't departments — they're stages of the same flow."
          />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
            {APPROACH.map((a, i) => (
              <Reveal key={a.title} delay={0.08 * i}>
                <div data-testid={`approach-${a.title.toLowerCase()}`} className="relative bg-navy-light border border-white/10 rounded-3xl p-8 lg:p-10 h-full overflow-hidden group hover:border-electric/40 transition-colors duration-300">
                  <span className="font-display font-black text-6xl text-outline absolute top-6 right-8" aria-hidden="true">{String(i + 1).padStart(2, "0")}</span>
                  <a.icon size={28} className="text-electric" />
                  <h3 className="mt-6 font-display text-2xl font-extrabold text-mist tracking-tight">{a.title}</h3>
                  <p className="mt-3 text-slatec leading-relaxed text-sm md:text-base">{a.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-16">
            <div className="relative rounded-3xl overflow-hidden border border-white/10">
              <img
                src="https://images.unsplash.com/photo-1603195827187-459ab02554a0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwzfHxjcmVhdGl2ZSUyMGFnZW5jeSUyMHRlYW18ZW58MHx8fHwxNzg3MzE5NzA4fDA&ixlib=rb-4.1.0&q=85"
                alt="A creative team collaborating on digital work"
                loading="lazy"
                className="w-full h-72 lg:h-96 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/70 to-transparent" aria-hidden="true" />
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="beliefs-section" className="bg-mist text-navy">
        <div className="max-w-8xl mx-auto px-6 lg:px-12 py-24 lg:py-36">
          <SectionHeading dark={false} eyebrow="What We Believe" title={<>Principles we <span className="text-brand">work by.</span></>} />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
            {BELIEFS.map((b, i) => (
              <Reveal key={b.title} delay={0.06 * i}>
                <div data-testid={`belief-${i}`} className="flex gap-5 bg-white border border-navy/10 rounded-3xl p-8 h-full hover:border-brand/40 transition-colors duration-300">
                  <b.icon size={24} className="text-brand shrink-0 mt-1" />
                  <div>
                    <h3 className="font-display text-xl lg:text-2xl font-extrabold tracking-tight">{b.title}</h3>
                    <p className="mt-2 text-navy/70 leading-relaxed text-sm md:text-base">{b.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="team" data-testid="team-section" className="bg-navy-deep grain relative scroll-mt-24">
        <div className="max-w-8xl mx-auto px-6 lg:px-12 py-24 lg:py-36">
          <div className="text-center">
            <Reveal>
              <p className="text-xs md:text-sm uppercase tracking-[0.25em] text-electric font-medium">Our Team</p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-display font-extrabold tracking-tighter text-3xl md:text-5xl text-mist max-w-3xl mx-auto leading-tight">
                The people behind <span className="text-electric">the click.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="mt-6 text-slatec max-w-xl mx-auto leading-relaxed">
                A small, hands-on team — directly involved in every project, from Street View shoots to social campaigns.
              </p>
            </Reveal>
          </div>
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {TEAM.map((m, i) => (
              <Reveal key={m.name} delay={0.06 * i}>
                <div
                  data-testid={`team-card-${teamSlug(m)}`}
                  className="group bg-navy-light border border-white/10 rounded-3xl overflow-hidden hover:border-electric/40 transition-colors duration-300"
                >
                  <div className="relative aspect-square overflow-hidden">
                    <img
                      src={`/assets/team/${m.photo}`}
                      alt={`${m.name} — ${m.role}, Click World Online`}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/60 via-transparent to-transparent" aria-hidden="true" />
                  </div>
                  <div className="p-6 text-center">
                    <h3 className="font-display text-xl font-extrabold tracking-tight text-mist">{m.name}</h3>
                    <p className="mt-1.5 text-electric text-xs uppercase tracking-[0.18em] font-medium">{m.role}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
};

export default About;
